import React, { useState, useEffect } from 'react';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import {
  Box,
  Button,
  ButtonGroup,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TablePagination,
  ListItem,
  ListItemText
} from '@material-ui/core';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import ViewListIcon from '@material-ui/icons/ViewList';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  BooleanParam,
  withDefault
} from 'use-query-params';
import { useHistory } from 'react-router-dom';
import { Capa } from './Capa.interface';
import { useGetCapaList } from './Capa.hook';
import CapaCreateDialog from './CapaCreateDialog';
import { formatDate } from '../../common/formatter';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import TaskBoard from '../tasks/TaskBoard';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';

const CapaPage: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [view, setView] = useQueryParam('view', StringParam);
  const [filter = '', setFilter] = useQueryParam('filter', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const [includeDeleted, setIncludeDeleted] = useQueryParam(
    'deleted',
    withDefault(BooleanParam, false)
  );
  const history = useHistory();

  const [{ data: capaList }, getCapa] = useGetCapaList();

  useEffect(() => {
    const params = {
      filter: filter,
      page: page,
      limit: limit,
      order: (order && order + '-' + orderDirection) || 'id-DESC',
      includeDeleted
    };
    getCapa(params);
  }, [page, limit, order, filter, getCapa, orderDirection, includeDeleted]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(+newPage);
  };

  const handleChangeRowPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(+event.target.value);
    setPage(0);
  };

  const close = () => {
    setShowPrompt(false);
    getCapa();
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>CAPA</h2>
        <Box ml={2}>
          <ButtonGroup variant='contained' color='primary'>
            <Button size='small' onClick={() => setView('List')}>
              <ViewListIcon />
            </Button>
            <Button size='small' onClick={() => setView('Board')}>
              <ViewColumnIcon />
            </Button>
          </ButtonGroup>
        </Box>
        {view !== 'Board' && (
          <>
            <SearchBox value={filter} onSearch={setFilter} />
            <PageTemplateHeaderActions>
              <Button
                variant='contained'
                color='primary'
                onClick={() => setShowPrompt(true)}
              >
                Add CAPA Form
              </Button>
              <ListMoreButtonMenu>
                <ListItem
                  button
                  onClick={() => setIncludeDeleted(!includeDeleted)}
                >
                  <ListItemText
                    primary={`${includeDeleted ? 'Hide' : 'Show'} Deleted`}
                  />
                </ListItem>
              </ListMoreButtonMenu>
            </PageTemplateHeaderActions>
          </>
        )}
      </PageTemplateHeader>

      {showPrompt && (
        <CapaCreateDialog
          open={showPrompt}
          //One for built-in backdrop click, one for cancel button click
          onClose={close}
          close={close}
        />
      )}
      {view === 'Board' ? (
        <PageTemplateDashboard>
          <TaskBoard
            entity={WorkflowEntity.Capa}
            onSelectTask={task => {
              const taskId = task.parent?.id || task.id;
              history.push(`/portal/capa/task/${taskId}`);
            }}
          />
        </PageTemplateDashboard>
      ) : (
        <PageTemplateCardOver>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'id'}
                      direction={getSortDirection('id')}
                      onClick={() => handleSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={order === 'title'}
                      direction={getSortDirection('title')}
                      onClick={() => handleSort('title')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'capaOwner'}
                      direction={getSortDirection('capaOwner')}
                      onClick={() => handleSort('capaOwner')}
                    >
                      CAPA Owner
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'capaStatus'}
                      direction={getSortDirection('capaStatus')}
                      onClick={() => handleSort('capaStatus')}
                    >
                      CAPA Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={order === 'modified'}
                      direction={getSortDirection('modified')}
                      onClick={() => handleSort('modified')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {capaList &&
                  capaList.data.map((capa: Capa) => (
                    <TableRow
                      hover
                      key={capa.id}
                      className={capa.deleted ? 'deleted' : ''}
                      onClick={() =>
                        history.push(`/portal/capa/task/${capa.taskId}`)
                      }
                    >
                      <TableCell>CAPA-{capa.id}</TableCell>
                      <TableCell>{capa.title}</TableCell>
                      <TableCell>
                        {typeof capa.capaOwner !== 'number' &&
                          capa.capaOwner &&
                          capa.capaOwner?.firstName +
                            ' ' +
                            capa.capaOwner?.lastName}
                      </TableCell>
                      <TableCell>
                        <TaskStatusLabel task={capa.task} />
                      </TableCell>
                      <TableCell>{formatDate(capa.modified)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={capaList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowPerPage}
          />
        </PageTemplateCardOver>
      )}
    </PageTemplate>
  );
};
export default CapaPage;
